/**
 * Better Auth client for player authentication.
 * Players log in with member_number + password (username plugin).
 *
 * This client manages the player's session via httpOnly cookies.
 * All requests already use credentials:'include' in api.js, so the
 * session cookie is sent automatically on every API call.
 */
import { createAuthClient } from 'better-auth/client';
import { usernameClient } from 'better-auth/client/plugins';

// In development the Vite proxy forwards /api → localhost:3001, so we
// can use a relative base URL. In production set VITE_API_BASE_URL if
// the API lives on a different origin.
const baseURL = import.meta.env.VITE_API_BASE_URL || '';

export const playerAuthClient = createAuthClient({
  baseURL,
  basePath: '/api/player-auth',
  plugins: [usernameClient()],
});

// ─── Convenience wrappers ──────────────────────────────────────────────────────

/** Sign in with member_number + password */
export async function playerSignIn(memberNumber, password) {
  return playerAuthClient.signIn.username({
    username: memberNumber.trim(),
    password,
  });
}

/** Sign out from Better Auth session */
export async function playerSignOut() {
  return playerAuthClient.signOut();
}

/** Get the current BA session (returns null if not authenticated) */
export async function getPlayerSession() {
  const { data } = await playerAuthClient.getSession();
  return data;
}

/** Request a password-reset email */
export async function requestPasswordReset(email) {
  // BA server endpoint is /request-password-reset (not /forget-password)
  return playerAuthClient.$fetch('/request-password-reset', {
    method: 'POST',
    body: { email, redirectTo: '/reset-password' },
  });
}

/** Apply a new password using the reset token */
export async function resetPassword(newPassword, token) {
  return playerAuthClient.resetPassword({ newPassword, token });
}
